/**
 * 白板渲染：直播画布与录像回放共用同一套绘制代码。
 *
 * 共用是刻意的 —— 老系统的实时白板与回放播放器是两套独立实现，
 * 结果是「上课时画的图，回放里样子不一样」这类问题反复出现。
 */

import { assetURL } from './endpoint'
import type { WBObject, WBPoint } from './whiteboard-types'
import { svgImageFor } from './svgAsset'

/** 画淡格线，帮助板书对齐。 */
export function drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.save()
  ctx.strokeStyle = '#ecece6'
  ctx.lineWidth = 1
  const step = w / 24
  for (let x = step; x < w; x += step) {
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, h)
    ctx.stroke()
  }
  for (let y = step; y < h; y += step) {
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(w, y)
    ctx.stroke()
  }
  ctx.restore()
}

/** 渲染一个图元。坐标按归一化 0-1 换算到画布尺寸。 */
export function drawObject(
  ctx: CanvasRenderingContext2D,
  o: WBObject,
  w: number,
  h: number,
  /** 矢量图异步加载完时回调，用来触发一次重绘 */
  onAssetReady?: () => void,
): void {
  const pts = (o.points ?? []).map((p) => ({ x: p.x * w, y: p.y * h }))
  if (!pts.length) return

  ctx.save()
  ctx.globalAlpha = o.style?.opacity ?? 1
  ctx.strokeStyle = o.style?.lineColor ?? '#1f2933'
  ctx.fillStyle = o.style?.fillColor || 'transparent'
  ctx.lineWidth = Math.max(1, (o.style?.lineWidth ?? 0.003) * w)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  if (o.style?.lineStyle === 'dash') ctx.setLineDash([8, 6])
  if (o.style?.lineStyle === 'dot') ctx.setLineDash([2, 5])

  switch (o.kind) {
    case 'pen':
    case 'poly': {
      ctx.beginPath()
      ctx.moveTo(pts[0]!.x, pts[0]!.y)
      // 二次贝塞尔平滑，避免高频采样下的锯齿折线。
      for (let i = 1; i < pts.length - 1; i++) {
        const mid = {
          x: (pts[i]!.x + pts[i + 1]!.x) / 2,
          y: (pts[i]!.y + pts[i + 1]!.y) / 2,
        }
        ctx.quadraticCurveTo(pts[i]!.x, pts[i]!.y, mid.x, mid.y)
      }
      const last = pts[pts.length - 1]!
      ctx.lineTo(last.x, last.y)
      ctx.stroke()
      break
    }

    case 'line': {
      ctx.beginPath()
      ctx.moveTo(pts[0]!.x, pts[0]!.y)
      const b = pts[1] ?? pts[0]!
      ctx.lineTo(b.x, b.y)
      ctx.stroke()
      break
    }

    case 'rect':
    case 'roundrect': {
      const [a, b] = [pts[0]!, pts[1] ?? pts[0]!]
      const x = Math.min(a.x, b.x)
      const y = Math.min(a.y, b.y)
      const rw = Math.abs(b.x - a.x)
      const rh = Math.abs(b.y - a.y)
      ctx.beginPath()
      if (o.kind === 'roundrect') {
        ctx.roundRect(x, y, rw, rh, Math.min(12, rw / 4, rh / 4))
      } else {
        ctx.rect(x, y, rw, rh)
      }
      if (o.style?.fillColor) ctx.fill()
      ctx.stroke()
      break
    }

    case 'ellipse': {
      const [a, b] = [pts[0]!, pts[1] ?? pts[0]!]
      ctx.beginPath()
      ctx.ellipse(
        (a.x + b.x) / 2,
        (a.y + b.y) / 2,
        Math.abs(b.x - a.x) / 2,
        Math.abs(b.y - a.y) / 2,
        0,
        0,
        Math.PI * 2,
      )
      if (o.style?.fillColor) ctx.fill()
      ctx.stroke()
      break
    }

    case 'text': {
      const size = (o.style?.fontSize ?? 0.03) * h
      const weight = o.style?.fontBold ? 'bold ' : ''
      const italic = o.style?.fontItalic ? 'italic ' : ''
      ctx.font = `${italic}${weight}${size}px ${o.style?.fontFamily ?? 'system-ui, sans-serif'}`
      ctx.fillStyle = o.style?.lineColor ?? '#1f2933'
      ctx.textBaseline = 'top'
      ctx.fillText(o.text ?? '', pts[0]!.x, pts[0]!.y)
      break
    }

    case 'image':
    case 'embed':
    case 'svg': {
      const [a, b] = [pts[0]!, pts[1] ?? pts[0]!]
      const x = Math.min(a.x, b.x)
      const y = Math.min(a.y, b.y)
      const iw = Math.abs(b.x - a.x)
      const ih = Math.abs(b.y - a.y)
      // 矢量图和位图在这里画法完全一样 —— 差别在拿到的 Image 是什么：
      // svg 那条路给的是 SVG 源，canvas 会按当前尺寸重绘矢量，放多大都不糊。
      const img = o.kind === 'svg' ? svgImageFor(o.blobUrl, onAssetReady) : imageFor(o.blobUrl)
      if (img?.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, x, y, iw, ih)
      } else {
        // 图片未加载完时画占位框，避免布局跳动。
        ctx.setLineDash([6, 4])
        ctx.strokeRect(x, y, iw, ih)
      }
      break
    }
  }
  ctx.restore()
}

/** 图片缓存，避免每帧重建 Image 触发重复请求。 */
const cache = new Map<string, HTMLImageElement>()

/** 取（并缓存）一张图片。 */
export function imageFor(url?: string): HTMLImageElement | null {
  if (!url) return null
  let img = cache.get(url)
  if (!img) {
    img = new Image()
    // 桌面端要补服务器前缀，否则相对路径会指向应用自身。
    img.src = assetURL(url)
    cache.set(url, img)
  }
  return img
}

/**
 * 画选中框。
 *
 * 用虚线包围盒而不是给图元本身换颜色 —— 换颜色会让人以为板书真的被改了，
 * 而选中只是本地状态，别人那边什么都没变。
 */
export function drawSelection(
  ctx: CanvasRenderingContext2D,
  obj: WBObject,
  w: number,
  h: number,
): void {
  // 图片与嵌入页可能没有 points（靠 url 定位），这类图元不画选中框。
  const pts = obj.points ?? []
  if (!pts.length) return
  const xs = pts.map((p) => p.x * w)
  const ys = pts.map((p) => p.y * h)

  const pad = 4
  const x0 = Math.min(...xs) - pad
  const y0 = Math.min(...ys) - pad
  const x1 = Math.max(...xs) + pad
  const y1 = Math.max(...ys) + pad

  ctx.save()
  ctx.strokeStyle = '#2f6fd0'
  ctx.lineWidth = 1
  ctx.setLineDash([5, 4])
  ctx.strokeRect(x0, y0, x1 - x0, y1 - y0)

  // 四角手柄，用来缩放。
  //
  // 只给「有面积」的图元画：自由笔迹是一串点，拉伸它没有意义，
  // 而且拉完笔锋粗细会跟着变形，比重画一遍还难看。
  if (isResizable(obj)) {
    ctx.setLineDash([])
    ctx.fillStyle = '#ffffff'
    ctx.strokeStyle = '#2f6fd0'
    ctx.lineWidth = 1.5
    for (const [hx, hy] of cornersOf(x0, y0, x1, y1)) {
      ctx.beginPath()
      ctx.rect(hx - HANDLE / 2, hy - HANDLE / 2, HANDLE, HANDLE)
      ctx.fill()
      ctx.stroke()
    }
  }
  ctx.restore()
}

/** 手柄边长（CSS 像素）。绘制与命中判定共用同一个值，避免「看得见点不着」。 */
export const HANDLE = 9

/** 命中判定比手柄本身宽松一点，鼠标不必分毫不差地压在方块上。 */
export const HANDLE_HIT = 14

/** 哪些图元能缩放。 */
export function isResizable(obj: WBObject): boolean {
  return obj.kind !== 'pen' && obj.kind !== 'poly' && (obj.points?.length ?? 0) >= 2
}

/** 包围盒的四角，顺序固定为 左上 / 右上 / 右下 / 左下。 */
export function cornersOf(x0: number, y0: number, x1: number, y1: number): Array<[number, number]> {
  return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]]
}

/** 选中框的包围盒（含内边距），单位是 CSS 像素。 */
export function boundsOf(obj: WBObject, w: number, h: number): [number, number, number, number] | null {
  const pts = obj.points ?? []
  if (!pts.length) return null
  const xs = pts.map((p) => p.x * w)
  const ys = pts.map((p) => p.y * h)
  const pad = 4
  return [Math.min(...xs) - pad, Math.min(...ys) - pad, Math.max(...xs) + pad, Math.max(...ys) + pad]
}

/**
 * 拖某个角缩放后的新框。
 *
 * 对角固定不动、被拖的角跟着指针 —— 所有画图工具都是这个行为。
 * 角序与 cornersOf 一致：0 左上 / 1 右上 / 2 右下 / 3 左下。
 *
 * 两个容易出错的地方：
 *  · 拖过对角之后框会反向，不归一化的话图元变成负尺寸，画不出来也点不中；
 *  · 缩到零之后图元再也选不上，只能清空整页 —— 所以留一个最小尺寸。
 */
export function resizeBoxByCorner(
  pts: WBPoint[],
  corner: number,
  to: WBPoint,
  min = 0.02,
): [WBPoint, WBPoint] | null {
  if (pts.length < 2) return null
  const x0 = Math.min(pts[0]!.x, pts[1]!.x)
  const y0 = Math.min(pts[0]!.y, pts[1]!.y)
  const x1 = Math.max(pts[0]!.x, pts[1]!.x)
  const y1 = Math.max(pts[0]!.y, pts[1]!.y)
  const fixed = {
    x: corner === 0 || corner === 3 ? x1 : x0,
    y: corner === 0 || corner === 1 ? y1 : y0,
  }
  const nx0 = Math.min(fixed.x, to.x)
  const ny0 = Math.min(fixed.y, to.y)
  const nx1 = Math.max(fixed.x, to.x)
  const ny1 = Math.max(fixed.y, to.y)
  if (nx1 - nx0 < min || ny1 - ny0 < min) return null
  return [{ x: nx0, y: ny0 }, { x: nx1, y: ny1 }]
}
